// @ts-strict-ignore
/* eslint-disable max-lines */
import { ClipboardEventHandler, KeyboardEventHandler, ReactNode, RefObject, SyntheticEvent, forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useFetchCommunityMentions } from 'src/utils/hooks/useFetchMentions';
import { parseNode } from 'src/utils/parseNode';
import Toolbar from 'src/components/textEditorSimple/components/toolbar';
import ImageEl from 'src/components/textEditor/components/image';
import Mentions, { debouncedCaptureMentionFactory, getMentionedUsers, isMentioning } from 'src/components/textEditorSimple/components/mentions';
import { titleLarge } from 'styles-js/mixins/typography';
import { ImageElement } from 'src/components/textEditor/customTypes';

// this is a simple editor to be used with androids only!
// this editor is simple as it uses the bare minimum required to make a working editor

export type Format = {
  'bold': boolean;
  'italic': boolean;
  'underline': boolean;
};
const defaultFormat: Format = {
  'bold': false,
  'italic': false,
  'underline': false
};
const defaultImage: ImageElement = {
  type: 'image',
  url: null,
  hash: null,
  file: null,
  description: null,
  children: null
};
type Props = {
  autoFocus?: boolean;
  dataTestId?: string;
  hasImage?: boolean;
  hasMentions?: boolean;
  hasTextFormat?: boolean;
  hasToolbar?: boolean;
  imgWithDescription?: boolean;
  initialHtml?: string;
  initialImage?: ImageElement;
  isReset?: boolean;
  isTitle?: boolean;
  onAddImg?: () => void;
  onChange?: () => void;
  onChanged: (editorData: unknown) => void;
  onFocus?: (e: SyntheticEvent) => void;
  placeholder: string;
  ref?: RefObject<HTMLDivElement>;
  submitButton?: ReactNode;
};
export default forwardRef(function ContentEditor({
  autoFocus = false,
  dataTestId: idProp,
  hasImage = true,
  hasMentions = true,
  hasTextFormat = true,
  hasToolbar = true,
  imgWithDescription = true,
  initialHtml,
  initialImage,
  isReset = false,
  isTitle = false,
  onAddImg,
  onChange,
  onChanged,
  // used to store current state (value) in the parent's useState
  onFocus = () => {},
  placeholder,
  submitButton
}: Props, ref: RefObject<HTMLDivElement>) {
  const id = useId();
  const dataTestId = idProp ?? id;
  const newRef = useRef(null);
  const editorRef = ref ?? newRef;
  const [format, setFormat] = useState<Format>(defaultFormat);
  const [keyPressing, setKeyPressing] = useState<Record<number, boolean>>({});
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [image, setImage] = useState(initialImage || defaultImage);
  const [isEmojiPickerClosed, setIsEmojiPickerClosed] = useState(false);
  const [target, setTarget] = useState<Range | null>(null); // range target where mention portal appears
  const [isFocused, setIsFocused] = useState(false);
  const {
    suggestedMentions,
    setSearchText,
    searchText = ''
  } = useFetchCommunityMentions();
  const onChangedUpdateState = () => {
    const body = parseNode(editorRef?.current).str;
    onChanged({
      body,
      mentionedUsers,
      image
    });
  };
  useEffect(() => {
    updateFormat();
    setIsFocused(editorRef?.current === window?.document?.activeElement);
    let focusTimeout;
    if (autoFocus) focusTimeout = setTimeout(() => editorRef?.current?.focus(), 500);
    return () => clearTimeout(focusTimeout);
  }, [editorRef, autoFocus]);
  const onFocusExtended = (e: SyntheticEvent) => {
    setIsFocused(true);
    onFocus(e);
  };
  const doOnBlur = useCallback(() => {
    setSearchText(''); // on blur, don't allow mentions adding !
    setIsFocused(false);
    setTarget(null);
    setKeyPressing({});
    setFormat(defaultFormat);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [setSearchText, setTarget, setKeyPressing, setFormat]);
  const debouncedCaptureMention = debouncedCaptureMentionFactory({
    target,
    setTarget,
    setSearchText,
    isFocused
  });
  const updateFormat = () => {
    setFormat({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline')
    });
  };
  const onKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    if (onChange) onChange();
    if (isMentioning({
      searchText,
      suggestedMentions
    }) || event.defaultPrevented) return;
    setTarget(null);
    const key = window.event ? event.which : event.keyCode;
    const firstChild = editorRef.current?.childNodes[0];
    const selObj = window.getSelection();
    setKeyPressing({
      ...keyPressing,
      [key]: true
    });
    switch (key) {
      case 8:
        // backspace
        if (firstChild === selObj?.focusNode && selObj?.focusNode === selObj?.anchorNode && selObj?.focusOffset === 0 && selObj?.anchorOffset === 0) {
          event.preventDefault();
          setKeyPressing({
            ...keyPressing,
            [key]: false
          });
        }
        break;
      case 13:
        // enter
        if (!hasTextFormat) return event.preventDefault();
        break;
      case 66:
        // b
        if (keyPressing[91] || keyPressing[93] || keyPressing[17]) {
          event.preventDefault();
          if (hasTextFormat) {
            document.execCommand('bold', false, null);
            setFormat({
              ...format,
              'bold': !format['bold']
            });
            setKeyPressing({
              ...keyPressing,
              [key]: false
            });
          }
        }
        break;
      case 73:
        // i
        if (keyPressing[91] || keyPressing[93] || keyPressing[17]) {
          event.preventDefault();
          if (hasTextFormat) {
            document.execCommand('italic', false, null);
            setFormat({
              ...format,
              'italic': !format['italic']
            });
            setKeyPressing({
              ...keyPressing,
              [key]: false
            });
          }
        }
        break;
      case 85:
        // u
        if (keyPressing[91] || keyPressing[93] || keyPressing[17]) {
          event.preventDefault();
          if (hasTextFormat) {
            document.execCommand('underline', false, null);
            setFormat({
              ...format,
              'underline': !format['underline']
            });
            setKeyPressing({
              ...keyPressing,
              [key]: false
            });
          }
        }
        break;
    }
  };
  const onKeyUp: KeyboardEventHandler<HTMLDivElement> = event => {
    onChangedUpdateState();
    const key = window.event ? event.which : event.keyCode;
    setKeyPressing({
      ...keyPressing,
      [key]: false
    });
    if (hasMentions) debouncedCaptureMention();
    // on backspace, if mentionedUsers, reevaluate if correct
    if (key === 8 && mentionedUsers) getMentionedUsers({
      editorRef,
      setMentionedUsers
    });
  };
  const onPaste: ClipboardEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    let text = event?.clipboardData?.getData('text/plain');
    // remove all new lines if no text formats allowed ! - e.g., write a post title
    if (!hasTextFormat) {
      text = text?.replace(/(\r\n|\n|\r)/gm, ' ');
    }
    document.execCommand('insertText', false, text);
    onChangedUpdateState();
  };
  const onClick = () => {
    if (hasMentions) debouncedCaptureMention();
    updateFormat();
  };
  useEffect(() => {
    if (isReset) {
      editorRef.current.innerHTML = '';
      setMentionedUsers([]);
      setImage(defaultImage);
      setIsEmojiPickerClosed(true);
      doOnBlur();
    }
  }, [isReset, doOnBlur, editorRef]);
  useEffect(() => onChangedUpdateState(), [image, mentionedUsers, format]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>
      {image?.url && <ImageEl element={image} imgWithDescription={imgWithDescription} setImageNonSlate={setImage} />}
      <Editor contentEditable="true" dangerouslySetInnerHTML={{
      __html: initialHtml ?? ''
    }} data-testid={dataTestId} isTitle={isTitle} placeholder={placeholder} ref={editorRef} onBlur={doOnBlur} onClick={onClick} onFocus={onFocusExtended} onKeyDown={onKeyDown} onKeyUp={onKeyUp} onPaste={onPaste} onTouchEnd={onClick} />
      {hasToolbar && <Toolbar dataTestId={dataTestId + '-toolbar'} editorRef={editorRef} flushChanges={onChangedUpdateState} format={format} hasImage={hasImage} hasMentions={hasMentions} isEmojiPickerClosed={isEmojiPickerClosed} // to use for closing from outside
    isFocused={isFocused} setFormat={setFormat} setUploadImage={setImage} submitButton={submitButton} uploadImgId={dataTestId + '-img'} onAddImg={onAddImg} />}
      {hasMentions && <Mentions mentionedUsers={mentionedUsers} searchText={searchText} setMentionedUsers={setMentionedUsers} setSearchText={setSearchText} setTarget={setTarget} suggestedMentions={suggestedMentions} target={target} />}
    </>;
});
const Editor = styled.div<{
  isTitle?: boolean;
  placeholder: string;
}>`
  white-space: pre-line;
  word-break: break-word;
  min-height: 30px;
  width: 100%;
  padding: 6px;
  overflow: scroll;
  >:first-child {
    padding: 6px 0;
  }
  &:focus-visible {
    outline: none;
  }

  &[contenteditable][placeholder]:empty:before {
    content: attr(placeholder);
    width: 100%; // some older androids break the text, making the area some 50px without specified width
    position: absolute;
    color: gray;
    background-color: transparent;
  }
  button {
    padding: 3px 3px 2px;
    margin: 0 1px;
    vertical-align: baseline;
    display: inline-block;
    border-radius: 4px;
    border: none;
    background-color: ${({
  theme
}) => theme.colorBlueBackground};
    box-shadow: none;
    color: ${({
  theme
}) => theme.colorTextLinkPrimary};

    &:hover, &:focus, &:active {
      box-shadow: ${({
  theme
}) => theme.shadowFocus};
    }
  }
  ${({
  isTitle
}) => isTitle && css`
    ${titleLarge};
    padding-right: 70px; // counter width on write post page
  `};
`;